// ============================================================================
// 고유 전술 보조 시스템: '상수(象手·상흔)' & '훈수(訓手·생존경고 및 연속수 코칭)' & '위기감지 방지권' 엔진
// 1. '상수(象手)':
//    - 상(象)의 1턴(하얀색 #FFFFFF), 2턴(초록색 #00E676), 3턴(빨간색 #FF1744) 이동 경로 표시
//    - 2턴 내에 상대 말을 잡을 수 있는 경로가 나오면 3턴까지 안 가고 거기에서 🎯 타겟팅 표시만!
// 2. '훈수(訓手)' 자격 검증(Eligibility - A01~A03):
//    - 초반 균형/무난한 국면(A01)이나 상대가 교환을 거부하면 이득이 사라지는 국면(A03)에서는
//      훈수 버튼을 'unavailable'로 잠그고 0회 차감
//    - 실제 전술 이득(+200cp 이상, 깊이 2~3 일관성 유지) 또는 주요 기물 구출/장군 해소가 검증된
//      국면(A02)에서만 'eligible'로 활성화
// 3. '호신강기 위기감지 방지권' (A04~A06):
//    - 희생타/미끼수(포 미끼 후 대차 포획, 차 희생 외통 등)는 다중 수읽기 비교로 오경고 0건(A04)
//    - 경고 시트 노출 및 '그대로 두기'/취소는 100% 무료(0회 차감, A05)
//    - 검증된 안전 대안수가 존재하고 유저가 '안전한 수 보기'를 눌렀을 때만 1회 차감(A06)
// ============================================================================

import {
  BoardGrid,
  Move,
  Piece,
  PieceType,
  Pos,
  Side,
  PIECE_NAMES_KO,
  PIECE_SCORES,
  applyMoveOnClone,
  cloneBoard,
  formatKoreanNotation,
  getAllLegalMoves,
  getBoardHash,
  getLegalMovesForPiece,
  getPseudoLegalMovesForPiece,
  isKingInCheck
} from './JanggiRules.js';
import {
  CP_VALUES,
  alphaBeta,
  analyzeTopMoves,
  evaluateBoardCp,
  isSquareDefendedBySide,
  quiescenceSearch
} from './JanggiAI.js';

export type TacticalVisionMode = 'sangsu' | 'hunsu';

export interface VisionStepNode {
  pos: Pos;
  turn: 1 | 2 | 3;
  color: '#FFFFFF' | '#00E676' | '#FF1744';
  fromPos: Pos;
  sourcePieceId: string;
  sourcePiecePos: Pos;
  isCapture: boolean;
  isTargetLock: boolean;
  stoppedFurtherExpansion: boolean;
  capturedPiece?: Piece | null;
  capturedValue?: number;
  pathHistory: Pos[];
}

export interface VisionBranch {
  from: Pos;
  to: Pos;
  turn: 1 | 2 | 3;
  color: '#FFFFFF' | '#00E676' | '#FF1744';
  isTargetLockBranch: boolean;
}

export interface EndangeredPieceAlert {
  piece: Piece;
  pos: Pos;
  attackerPiece: Piece;
  attackerPos: Pos;
  pieceNameKo: string;
  attackerNameKo: string;
  dangerReason: string;
}

export interface HunsuCoachingData {
  modeType: 'survival_emergency' | 'tactical_combo';
  eligibilityStatus?: 'eligible' | 'unavailable';
  eligibilityReasonCode?: string;
  netGainCp?: number;
  endangeredList: EndangeredPieceAlert[];
  primaryAlert: EndangeredPieceAlert | null;
  step1Move: Move;
  predictedOpponentReply?: Move;
  step2FollowUpMove?: Move;
  headline: string;
  step1Description: string;
  opponentReplyDescription: string;
  step2Description: string;
}

export interface TacticalVisionResult {
  mode: TacticalVisionMode;
  side: Side;
  targetPieceType: PieceType;
  nodes: VisionStepNode[];
  branches: VisionBranch[];
  earlyCaptureTargets: VisionStepNode[];
  hunsu?: HunsuCoachingData;
  summaryText: string;
}

export interface HunsuEligibilityResult {
  status: 'eligible' | 'unavailable';
  analysisId: string;
  boardHash: string;
  reasonCode:
    | 'tactical_opportunity'
    | 'survival_rescue'
    | 'check_evasion'
    | 'quiet_balanced_position'
    | 'exchange_refusal_collapses'
    | 'no_legal_moves';
  reasonText: string;
  netGainCp: number;
  stabilityDiffCp: number;
  coaching?: HunsuCoachingData;
}

export const VISION_TURN_COLORS: Record<1 | 2 | 3, '#FFFFFF' | '#00E676' | '#FF1744'> = {
  1: '#FFFFFF',
  2: '#00E676',
  3: '#FF1744'
};

// ============================================================================
// 1. 졸(卒/兵) 제외 내 중요 기물(차·포·마·상·사) 즉각 피격 위험 탐지
// ============================================================================
export function detectEndangeredNonPawnPieces(
  board: BoardGrid,
  mySide: Side
): EndangeredPieceAlert[] {
  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const enemyLegalMoves = getAllLegalMoves(board, enemySide);
  const alerts: EndangeredPieceAlert[] = [];

  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const myPiece = board[r][c];
      if (!myPiece || myPiece.side !== mySide) continue;
      if (myPiece.type === 'jol' || myPiece.type === 'king') continue;

      const capturingEnemyMoves = enemyLegalMoves.filter(
        em => em.to.row === r && em.to.col === c
      );
      if (capturingEnemyMoves.length === 0) continue;

      capturingEnemyMoves.sort(
        (a, b) => CP_VALUES[a.piece.type] - CP_VALUES[b.piece.type]
      );
      const worstThreat = capturingEnemyMoves[0];
      const defended = isSquareDefendedBySide(
        board,
        { row: r, col: c },
        mySide,
        worstThreat.from
      );

      const myVal = CP_VALUES[myPiece.type];
      const atkVal = CP_VALUES[worstThreat.piece.type];

      if (!defended || myVal > atkVal + 80) {
        const pieceNameKo = `${PIECE_NAMES_KO[myPiece.type][mySide]}(${PIECE_SCORES[myPiece.type]}점)`;
        const attackerNameKo = `상대 ${PIECE_NAMES_KO[worstThreat.piece.type][enemySide]}`;
        const coordStr = `${c + 1}·${r + 1}`;
        const atkCoordStr = `${worstThreat.from.col + 1}·${worstThreat.from.row + 1}`;

        const dangerReason = !defended
          ? `🚨 위기! 내 '${pieceNameKo}'(${coordStr})이(가) 보호가 없어 지금 안 피하면 ${attackerNameKo}(${atkCoordStr})에게 공짜로 죽습니다!`
          : `🚨 위기! 내 귀중한 '${pieceNameKo}'(${coordStr})이(가) ${attackerNameKo}(${atkCoordStr})의 공격선상에 놓여 지금 대처하지 않으면 바로 잡힙니다!`;

        alerts.push({
          piece: myPiece,
          pos: { row: r, col: c },
          attackerPiece: worstThreat.piece,
          attackerPos: worstThreat.from,
          pieceNameKo,
          attackerNameKo,
          dangerReason
        });
      }
    }
  }

  alerts.sort((a, b) => CP_VALUES[b.piece.type] - CP_VALUES[a.piece.type]);
  return alerts;
}

// ============================================================================
// 2. 훈수 자격 판정 엔진 (evaluateHunsuEligibility - A01, A02, A03)
// - A01: 초반 균형 국면 등 뚜렷한 전술 이득(< 200cp)이 없으면 'unavailable' ("지금은 어느 수를 두어도 무난해요")
// - A02: 상대 최선 응수 이후에도 +200cp 이상 순이득(포획·양걸이·외통·주요 기물 구출)이 유지되면 'eligible'
// - A03: 1수 앞 포획처럼 보여도 상대가 교환을 거부하거나 회피했을 때 이득이 사라지면 'unavailable'
// ============================================================================
export function evaluateHunsuEligibility(
  board: BoardGrid,
  side: Side
): HunsuEligibilityResult {
  const boardHash = getBoardHash(board, side);
  const shortHash = Array.from(boardHash).reduce((acc, ch) => ((acc * 31 + ch.charCodeAt(0)) >>> 0), 7).toString(36);
  const analysisId = `anl_${side}_${shortHash}`;

  const legalMoves = getAllLegalMoves(board, side);
  if (legalMoves.length === 0) {
    return {
      status: 'unavailable',
      analysisId,
      boardHash,
      reasonCode: 'no_legal_moves',
      reasonText: '현재 둘 수 있는 합법적인 수가 없어요.',
      netGainCp: 0,
      stabilityDiffCp: 0
    };
  }

  const inCheckNow = isKingInCheck(board, side);
  const endangeredList = detectEndangeredNonPawnPieces(board, side);
  const baselineCp = evaluateBoardCp(board, side);

  // Depth 3 multi-ply evaluation to verify opponent's best defense / exchange refusal (single shared pass)
  const topEvaluations = analyzeTopMoves(board, side, 3, 8);
  const safeEvaluations = topEvaluations.filter(e => !e.isSelfTrap);
  const bestPool = safeEvaluations.length > 0 ? safeEvaluations : topEvaluations;
  const best = bestPool[0];
  let cachedCoaching: HunsuCoachingData | undefined;
  const getCoaching = () => {
    if (!cachedCoaching) {
      cachedCoaching = computeHunsuCoaching(board, side, topEvaluations).hunsu;
    }
    return cachedCoaching;
  };

  if (inCheckNow) {
    const coaching = getCoaching();
    if (coaching) {
      return {
        status: 'eligible',
        analysisId,
        boardHash,
        reasonCode: 'check_evasion',
        reasonText: '왕이 장군 위협을 받고 있어 정확한 멍군 해법이 준비되었어요.',
        netGainCp: Math.max(200, (best.deepScoreCp ?? best.scoreCp) - baselineCp),
        stabilityDiffCp: 0,
        coaching
      };
    }
  }

  if (endangeredList.length > 0) {
    const coaching = getCoaching();
    if (coaching) {
      const primaryAlert = endangeredList[0];
      const { nextBoard } = applyMoveOnClone(board, coaching.step1Move.from, coaching.step1Move.to);
      const rescued = !detectEndangeredNonPawnPieces(nextBoard, side).some(
        a => a.piece.id === primaryAlert.piece.id
      );
      if (rescued) {
        return {
          status: 'eligible',
          analysisId,
          boardHash,
          reasonCode: 'survival_rescue',
          reasonText: `위험에 처한 ${primaryAlert.pieceNameKo}을(를) 구출하는 수가 있어요.`,
          netGainCp: CP_VALUES[primaryAlert.piece.type],
          stabilityDiffCp: 0,
          coaching
        };
      }
    }
  }

  const shallowGain = (best.shallowScoreCp ?? best.scoreCp) - baselineCp;
  const deepGain = (best.deepScoreCp ?? best.scoreCp) - baselineCp;
  const stabilityDiffCp = Math.abs((best.deepScoreCp ?? best.scoreCp) - (best.shallowScoreCp ?? best.scoreCp));

  // Compute quiet reference score (best non-capturing, non-checking move or 4th move)
  const quietEval = bestPool.find(e => !e.move.captured && !e.move.gaveCheck);
  const quietCp = quietEval ? (quietEval.deepScoreCp ?? quietEval.scoreCp) : baselineCp;
  const gainOverQuiet = (best.deepScoreCp ?? best.scoreCp) - quietCp;

  // Check if a shallow threat collapses when opponent refuses the exchange or defends properly (A03)
  const hasTacticalIntent =
    Boolean(best.move.captured) ||
    Boolean(best.move.gaveCheck) ||
    Boolean(best.bestFollowUpMove?.captured) ||
    Boolean(best.bestFollowUpMove?.gaveCheck);

  if (hasTacticalIntent && shallowGain >= 180 && deepGain < 180) {
    return {
      status: 'unavailable',
      analysisId,
      boardHash,
      reasonCode: 'exchange_refusal_collapses',
      reasonText: '상대가 교환을 피하거나 방어하면 뚜렷한 이득이 남지 않는 국면이에요.',
      netGainCp: deepGain,
      stabilityDiffCp
    };
  }

  // Genuine tactical opportunity (A02):
  // Either a direct capture/checkmate/double-attack that retains >= 200cp net gain after opponent's best defense,
  // or a forced tactical sequence that beats quiet development by >= 200cp.
  const isMateThreat = (best.deepScoreCp ?? best.scoreCp) >= 9000;
  const isVerifiedTacticalGain =
    isMateThreat ||
    (deepGain >= 200 && (hasTacticalIntent || gainOverQuiet >= 180) && stabilityDiffCp <= 350);

  if (isVerifiedTacticalGain) {
    const coaching = getCoaching();
    if (coaching) {
      coaching.eligibilityStatus = 'eligible';
      coaching.eligibilityReasonCode = 'tactical_opportunity';
      coaching.netGainCp = deepGain;
      return {
        status: 'eligible',
        analysisId,
        boardHash,
        reasonCode: 'tactical_opportunity',
        reasonText: '지금 바로 이득을 볼 수 있는 확실한 전술 수가 있어요.',
        netGainCp: deepGain,
        stabilityDiffCp,
        coaching
      };
    }
  }

  // Otherwise, quiet / balanced position (A01)
  return {
    status: 'unavailable',
    analysisId,
    boardHash,
    reasonCode: 'quiet_balanced_position',
    reasonText: '지금은 어느 수를 두어도 무난해요.',
    netGainCp: deepGain,
    stabilityDiffCp
  };
}

// ============================================================================
// 2-B. '훈수(訓手)' 코칭 데이터 생성
// ============================================================================
export function computeHunsuCoaching(
  board: BoardGrid,
  side: Side,
  precomputedEvaluations?: ReturnType<typeof analyzeTopMoves>
): TacticalVisionResult {
  const enemySide: Side = side === 'cho' ? 'han' : 'cho';
  const inCheckNow = isKingInCheck(board, side);
  const endangeredList = detectEndangeredNonPawnPieces(board, side);

  const topEvaluations = precomputedEvaluations ?? analyzeTopMoves(board, side, 3, 8);
  const safeEvaluations = topEvaluations.filter(e => !e.isSelfTrap);
  const bestPool = safeEvaluations.length > 0 ? safeEvaluations : topEvaluations;

  if (bestPool.length === 0) {
    return {
      mode: 'hunsu',
      side,
      targetPieceType: 'king',
      nodes: [],
      branches: [],
      earlyCaptureTargets: [],
      summaryText: '현재 둘 수 있는 합법적인 수가 없습니다.'
    };
  }

  let chosenEval = bestPool[0];
  const primaryAlert: EndangeredPieceAlert | null = endangeredList[0] || null;
  const modeType: 'survival_emergency' | 'tactical_combo' =
    inCheckNow || primaryAlert ? 'survival_emergency' : 'tactical_combo';

  if (primaryAlert && !inCheckNow) {
    const dangerId = primaryAlert.piece.id;
    const rescueCandidates = bestPool.filter(ev => {
      const { nextBoard } = applyMoveOnClone(board, ev.move.from, ev.move.to);
      const stillEndangered = detectEndangeredNonPawnPieces(nextBoard, side).some(
        a => a.piece.id === dangerId
      );
      return !stillEndangered;
    });

    if (rescueCandidates.length > 0 && rescueCandidates[0].scoreCp >= bestPool[0].scoreCp - 150) {
      chosenEval = rescueCandidates[0];
    }
  }

  const step1 = chosenEval.move;
  const oppReply = chosenEval.bestOpponentReply;
  const step2 = chosenEval.bestFollowUpMove;

  const step1Notation = step1.notation || formatKoreanNotation(step1.piece, step1.from, step1.to, step1.captured);
  const oppNotation = oppReply
    ? oppReply.notation || formatKoreanNotation(oppReply.piece, oppReply.from, oppReply.to, oppReply.captured)
    : '상대 방어 응수';
  const step2Notation = step2
    ? step2.notation || formatKoreanNotation(step2.piece, step2.from, step2.to, step2.captured)
    : '';

  let headline = '';
  let step1Description = '';
  let opponentReplyDescription = '';
  let step2Description = '';

  if (inCheckNow) {
    headline = `🚨 [왕 장군 위기!] 지금 즉시 왕(宮)을 보호하는 멍군을 두어야 합니다!`;
    step1Description = `① 지금 둘 생존수: [${step1Notation}] 수로 장군을 막아내세요!`;
  } else if (primaryAlert) {
    headline = primaryAlert.dangerReason;
    const movedDangerPiece =
      step1.from.row === primaryAlert.pos.row && step1.from.col === primaryAlert.pos.col;
    const capturedAttacker =
      step1.to.row === primaryAlert.attackerPos.row && step1.to.col === primaryAlert.attackerPos.col;

    if (capturedAttacker) {
      step1Description = `① 지금 둘 수 (공격자 선제 타격!): 내 말을 노리던 ${primaryAlert.attackerNameKo}을(를) [${step1Notation}] 수로 먼저 잡아 위기를 없애세요!`;
    } else if (movedDangerPiece) {
      step1Description = `① 지금 둘 수 (즉시 회피!): 죽을 위기인 내 ${primaryAlert.pieceNameKo}을(를) [${step1Notation}] 자리로 안전하게 피하세요!`;
    } else {
      step1Description = `① 지금 둘 수 (길목 차단/맞대응): [${step1Notation}] 수로 상대의 공격 길을 막거나 맞불을 놓아 내 ${primaryAlert.pieceNameKo}을(를) 지키세요!`;
    }
  } else {
    headline = `💡 [AI 연계 훈수] 한 수로는 안 풀립니다! ①번으로 포석을 연 뒤 ②번 후속타로 공략하세요!`;
    const step1Effect = step1.captured
      ? `상대 ${PIECE_NAMES_KO[step1.captured.type][enemySide]}을(를) 먼저 제압하며`
      : step1.gaveCheck
      ? `장군을 부르며 상대 진영을 흔들고`
      : `다음 공격을 위한 핵심 길목을 확보하며`;
    step1Description = `① 지금 둘 추천수: [${step1Notation}] — ${step1Effect} 주도권을 잡으세요.`;
  }

  opponentReplyDescription = oppReply
    ? `↳ 상대가 이에 맞서 [${oppNotation}] 수로 응수하면,`
    : `↳ 상대가 방어 수를 두더라도,`;

  if (step2) {
    const step2Effect = step2.captured
      ? `상대 '${PIECE_NAMES_KO[step2.captured.type][enemySide]}(${PIECE_SCORES[step2.captured.type]}점)'을(를) 포획하여 결정적 이득을 거둡니다!`
      : step2.gaveCheck
      ? `강력한 장군(將軍) 공격으로 상대 궁성을 무너뜨립니다!`
      : `공격 진형을 완성하여 판세를 유리하게 풀어갑니다!`;
    step2Description = `② 그 다음 내 수 (연계 후속타!): 이어서 [${step2Notation}] 수를 두면 ${step2Effect}`;
  } else {
    step2Description = `② 그 다음 내 수: 확보한 중앙 주도권을 바탕으로 상대 약점을 계속 압박하세요!`;
  }

  const hunsuData: HunsuCoachingData = {
    modeType,
    endangeredList,
    primaryAlert,
    step1Move: step1,
    predictedOpponentReply: oppReply,
    step2FollowUpMove: step2,
    headline,
    step1Description,
    opponentReplyDescription,
    step2Description
  };

  return {
    mode: 'hunsu',
    side,
    targetPieceType: step1.piece.type,
    nodes: [],
    branches: [],
    earlyCaptureTargets: [],
    hunsu: hunsuData,
    summaryText: `${headline} ➜ ${step1Description}`
  };
}

// ============================================================================
// 3. '상수(象手)' 3턴 경로 탐색 엔진 (+ 2턴 내 포획 시 3턴 미확장 & 🎯 조기 타겟팅 마감)
// ============================================================================
export function computeSangsuMasuVision(
  board: BoardGrid,
  side: Side,
  mode: TacticalVisionMode = 'sangsu',
  specificPiecePos?: Pos
): TacticalVisionResult {
  if (mode === 'hunsu') {
    return computeHunsuCoaching(board, side);
  }

  const targetType: PieceType = 'sang';
  const nodesMap = new Map<string, VisionStepNode>();
  const branches: VisionBranch[] = [];
  const earlyCaptureTargets: VisionStepNode[] = [];

  const sources: { pos: Pos; piece: Piece }[] = [];
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      if (specificPiecePos && (specificPiecePos.row !== r || specificPiecePos.col !== c)) continue;
      const p = board[r][c];
      if (p && p.side === side && p.type === targetType) {
        sources.push({ pos: { row: r, col: c }, piece: p });
      }
    }
  }

  const keyOf = (p: Pos) => `${p.row},${p.col}`;

  for (const src of sources) {
    const p0 = src.pos;
    const turn1Moves = getLegalMovesForPiece(board, p0);

    for (const p1 of turn1Moves) {
      const occ1 = board[p1.row][p1.col];
      const isCap1 = Boolean(occ1 && occ1.side !== side);

      const node1: VisionStepNode = {
        pos: p1,
        turn: 1,
        color: VISION_TURN_COLORS[1],
        fromPos: p0,
        sourcePieceId: src.piece.id,
        sourcePiecePos: p0,
        isCapture: isCap1,
        isTargetLock: isCap1,
        stoppedFurtherExpansion: isCap1,
        capturedPiece: isCap1 ? occ1 : null,
        capturedValue: isCap1 && occ1 ? PIECE_SCORES[occ1.type] : 0,
        pathHistory: [p0, p1]
      };

      branches.push({
        from: p0,
        to: p1,
        turn: 1,
        color: VISION_TURN_COLORS[1],
        isTargetLockBranch: isCap1
      });

      const k1 = keyOf(p1);
      if (!nodesMap.has(k1) || node1.isTargetLock) {
        nodesMap.set(k1, node1);
      }

      if (isCap1) {
        earlyCaptureTargets.push(node1);
        continue;
      }

      const boardAfter1 = cloneBoard(board);
      boardAfter1[p1.row][p1.col] = src.piece;
      boardAfter1[p0.row][p0.col] = null;

      const turn2Moves = getPseudoLegalMovesForPiece(boardAfter1, p1, src.piece).filter(
        p2 => !(p2.row === p0.row && p2.col === p0.col)
      );

      for (const p2 of turn2Moves) {
        const occ2 = boardAfter1[p2.row][p2.col];
        const isCap2 = Boolean(occ2 && occ2.side !== side);

        const node2: VisionStepNode = {
          pos: p2,
          turn: 2,
          color: VISION_TURN_COLORS[2],
          fromPos: p1,
          sourcePieceId: src.piece.id,
          sourcePiecePos: p0,
          isCapture: isCap2,
          isTargetLock: isCap2,
          stoppedFurtherExpansion: isCap2,
          capturedPiece: isCap2 ? occ2 : null,
          capturedValue: isCap2 && occ2 ? PIECE_SCORES[occ2.type] : 0,
          pathHistory: [p0, p1, p2]
        };

        branches.push({
          from: p1,
          to: p2,
          turn: 2,
          color: VISION_TURN_COLORS[2],
          isTargetLockBranch: isCap2
        });

        const k2 = keyOf(p2);
        const existing2 = nodesMap.get(k2);
        if (!existing2 || existing2.turn > 2 || (existing2.turn === 2 && isCap2 && !existing2.isTargetLock)) {
          nodesMap.set(k2, node2);
        }

        if (isCap2) {
          earlyCaptureTargets.push(node2);
          continue;
        }

        const boardAfter2 = cloneBoard(boardAfter1);
        boardAfter2[p2.row][p2.col] = src.piece;
        boardAfter2[p1.row][p1.col] = null;

        const turn3Moves = getPseudoLegalMovesForPiece(boardAfter2, p2, src.piece).filter(
          p3 => !(p3.row === p1.row && p3.col === p1.col) && !(p3.row === p0.row && p3.col === p0.col)
        );

        for (const p3 of turn3Moves) {
          const occ3 = boardAfter2[p3.row][p3.col];
          const isCap3 = Boolean(occ3 && occ3.side !== side);

          const node3: VisionStepNode = {
            pos: p3,
            turn: 3,
            color: VISION_TURN_COLORS[3],
            fromPos: p2,
            sourcePieceId: src.piece.id,
            sourcePiecePos: p0,
            isCapture: isCap3,
            isTargetLock: false,
            stoppedFurtherExpansion: false,
            capturedPiece: isCap3 ? occ3 : null,
            capturedValue: isCap3 && occ3 ? PIECE_SCORES[occ3.type] : 0,
            pathHistory: [p0, p1, p2, p3]
          };

          branches.push({
            from: p2,
            to: p3,
            turn: 3,
            color: VISION_TURN_COLORS[3],
            isTargetLockBranch: false
          });

          const k3 = keyOf(p3);
          if (!nodesMap.has(k3)) {
            nodesMap.set(k3, node3);
          }
        }
      }
    }
  }

  const allNodes = Array.from(nodesMap.values());
  let summaryText = `🐘 상수(象手·상흔) 가동: 상(象)의 1턴(하얀색) → 2턴(초록색) → 3턴(빨간색) 진격 경로를 표시합니다.`;
  if (earlyCaptureTargets.length > 0) {
    const bestTarget = [...earlyCaptureTargets].sort((a, b) => (b.capturedValue || 0) - (a.capturedValue || 0))[0];
    const targetName = bestTarget.capturedPiece
      ? PIECE_NAMES_KO[bestTarget.capturedPiece.type][bestTarget.capturedPiece.side]
      : '상대 기물';
    summaryText = `🎯 [상수(象手) 포착!] ${bestTarget.turn}턴 만에 상대 '${targetName}'(${bestTarget.pos.col + 1}·${bestTarget.pos.row + 1}) 타격 가능! (3턴 생략·조준 완료)`;
  }

  return {
    mode: 'sangsu',
    side,
    targetPieceType: targetType,
    nodes: allNodes,
    branches,
    earlyCaptureTargets,
    summaryText
  };
}

// ============================================================================
// 4. '호신강기(護身罡氣) — 자동 위기감지 방지권' 엔진 (A04, A05, A06)
// ============================================================================

export type GuardablePieceType = 'cha' | 'po' | 'sa' | 'ma' | 'sang';

export type CrisisGuardConfig = Record<GuardablePieceType, boolean>;

export function createDefaultCrisisGuardConfig(): CrisisGuardConfig {
  return {
    cha: true,
    po: true,
    sa: true,
    ma: false,
    sang: false
  };
}

export interface CrisisInterventionResult {
  triggered: boolean;
  crisisType: 'immediate_1turn_loss' | 'collapse_2turn_trap';
  guardedPiece: Piece;
  guardedPiecePos: Pos;
  guardedPieceNameKo: string;
  netLossCp: number;
  enemyStrikeMove1: Move;
  myForcedReply?: Move;
  enemyFatalCaptureMove2?: Move;
  recommendedSafeMove: Move;
  recommendedFollowUp?: Move;
  warningIsFree: boolean;             // ★ A05: 경고 표시 자체는 항상 0회 차감
  hasVerifiedSafeAlternative: boolean;// ★ A06: 검증된 안전 대안수가 실제로 존재하는지 여부
  canDeductForSafeReveal: boolean;    // ★ A06: '안전한 수 보기' 클릭 시 1회 차감 가능 여부
  moveKey: string;                    // ★ A05: '그대로 두기' 선택 시 동일 수 재경고 억제 키
  headline: string;
  explanation: string;
}

function isPieceGuardEnabled(piece: Piece, guardConfig: CrisisGuardConfig): boolean {
  if (piece.type === 'jol' || piece.type === 'king') return false;
  return Boolean(guardConfig[piece.type as GuardablePieceType]);
}

function computeRecaptureGainCp(
  boardAfterOppCap: BoardGrid,
  captureSquare: Pos,
  enemyAttackerPiece: Piece,
  mySide: Side
): number {
  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const myRecaptures = getAllLegalMoves(boardAfterOppCap, mySide).filter(
    rm => rm.to.row === captureSquare.row && rm.to.col === captureSquare.col
  );
  if (myRecaptures.length === 0) return 0;

  myRecaptures.sort((a, b) => CP_VALUES[a.piece.type] - CP_VALUES[b.piece.type]);
  const bestRecap = myRecaptures[0];
  const attackerVal = CP_VALUES[enemyAttackerPiece.type];
  const myRecapPieceVal = CP_VALUES[bestRecap.piece.type];

  const { nextBoard: boardAfterMyRecap } = applyMoveOnClone(
    boardAfterOppCap,
    bestRecap.from,
    bestRecap.to
  );
  const oppReRecaptures = getAllLegalMoves(boardAfterMyRecap, enemySide).filter(
    em => em.to.row === captureSquare.row && em.to.col === captureSquare.col
  );

  if (oppReRecaptures.length === 0) {
    return attackerVal;
  }

  return Math.max(0, attackerVal - myRecapPieceVal);
}

/**
 * 상대가 내 기물을 잡은 직후(`boardAfterOppCap`), 같은 칸 되잡기뿐 아니라
 * 다른 칸의 상대 대차 포획이나 외통수 등 보드 전체에서 내가 거둘 수 있는 최대 반격 이득(cp)을 계산 (A04)
 */
function computeBestCounterStrikeGainCp(
  boardBefore: BoardGrid,
  boardAfterOppCap: BoardGrid,
  mySide: Side,
  victimVal: number,
  myInitialGainCp: number
): number {
  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const myMoves = getAllLegalMoves(boardAfterOppCap, mySide);
  if (myMoves.length === 0) return 0;

  const baselineCp = evaluateBoardCp(boardBefore, mySide);
  let bestCounterGain = 0;

  // Check tactical counter-strikes (captures or checks)
  const tacticalCounters = myMoves.filter(m => m.captured || m.gaveCheck);
  for (const cm of tacticalCounters) {
    const { nextBoard: afterCounter } = applyMoveOnClone(boardAfterOppCap, cm.from, cm.to);
    const oppReplies = getAllLegalMoves(afterCounter, enemySide);
    if (oppReplies.length === 0 && isKingInCheck(afterCounter, enemySide)) {
      // Counter-strike delivers immediate checkmate!
      return 19000;
    }
    const postCounterCp = quiescenceSearch(afterCounter, -Infinity, Infinity, enemySide, mySide, 2);
    // If after giving up `victim` and executing `cm`, our board evaluation is at least as good as baseline
    // (or recovers >= victimVal - 100), then `victim` was a tactical sacrifice / decoy!
    const netChangeFromStart = postCounterCp - baselineCp;
    if (netChangeFromStart >= -100) {
      bestCounterGain = Math.max(bestCounterGain, victimVal - myInitialGainCp + netChangeFromStart);
    } else if (cm.captured) {
      const capVal = CP_VALUES[cm.captured.type];
      const defended = isSquareDefendedBySide(afterCounter, cm.to, enemySide);
      const netCounterCap = defended ? Math.max(0, capVal - CP_VALUES[cm.piece.type]) : capVal;
      if (netCounterCap > bestCounterGain) {
        bestCounterGain = netCounterCap;
      }
    }
  }

  return bestCounterGain;
}

export function formatMoveKey(move: { from: Pos; to: Pos }): string {
  return `${move.from.row},${move.from.col}->${move.to.row},${move.to.col}`;
}

/**
 * < 0.5ms 초고속 위기 가능성 사전 판별:
 * 이 함수가 false를 반환하면 detectMoveTriggersMultiTurnCrisis는 수학적으로 100% null이므로
 * 워커 왕복이나 alpha-beta 탐색 없이 pointerdown 즉시(0ms) 착수를 실행할 수 있습니다.
 */
export function canMovePossiblyTriggerCrisisFast(
  boardBefore: BoardGrid,
  candidateMove: Move,
  mySide: Side,
  guardConfig: CrisisGuardConfig,
  suppressedMoveKeys?: Set<string>
): boolean {
  const moveKey = formatMoveKey(candidateMove);
  const boardHash = getBoardHash(boardBefore, mySide);
  if (suppressedMoveKeys && (suppressedMoveKeys.has(moveKey) || suppressedMoveKeys.has(`${boardHash}:${moveKey}`))) {
    return false;
  }

  const { nextBoard: boardAfterMyMove } = applyMoveOnClone(
    boardBefore,
    candidateMove.from,
    candidateMove.to
  );

  let hasAnyGuardedPiece = false;
  for (let r = 0; r < 10 && !hasAnyGuardedPiece; r++) {
    for (let c = 0; c < 9; c++) {
      const p = boardAfterMyMove[r][c];
      if (p && p.side === mySide && isPieceGuardEnabled(p, guardConfig)) {
        hasAnyGuardedPiece = true;
        break;
      }
    }
  }
  if (!hasAnyGuardedPiece) return false;

  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const oppMoves1 = getAllLegalMoves(boardAfterMyMove, enemySide);
  if (oppMoves1.length === 0) return false;

  const myInitialGainCp = candidateMove.captured ? CP_VALUES[candidateMove.captured.type] : 0;

  for (const oppM1 of oppMoves1) {
    if (oppM1.gaveCheck) return true;
    if (oppM1.captured) {
      const capVal = CP_VALUES[oppM1.captured.type];
      if (capVal >= 300) return true;
      if (
        oppM1.captured.side === mySide &&
        isPieceGuardEnabled(oppM1.captured, guardConfig) &&
        capVal - myInitialGainCp >= 280
      ) {
        return true;
      }
    }
  }

  return false;
}

export function detectMoveTriggersMultiTurnCrisis(
  boardBefore: BoardGrid,
  candidateMove: Move,
  mySide: Side,
  guardConfig: CrisisGuardConfig,
  suppressedMoveKeys?: Set<string>
): CrisisInterventionResult | null {
  const moveKey = formatMoveKey(candidateMove);
  const boardHash = getBoardHash(boardBefore, mySide);
  if (suppressedMoveKeys && (suppressedMoveKeys.has(moveKey) || suppressedMoveKeys.has(`${boardHash}:${moveKey}`))) {
    return null;
  }

  const enemySide: Side = mySide === 'cho' ? 'han' : 'cho';
  const myInitialGainCp = candidateMove.captured ? CP_VALUES[candidateMove.captured.type] : 0;
  const baselineCp = evaluateBoardCp(boardBefore, mySide);

  const { nextBoard: boardAfterMyMove } = applyMoveOnClone(
    boardBefore,
    candidateMove.from,
    candidateMove.to
  );

  let hasAnyGuardedPiece = false;
  for (let r = 0; r < 10 && !hasAnyGuardedPiece; r++) {
    for (let c = 0; c < 9; c++) {
      const p = boardAfterMyMove[r][c];
      if (p && p.side === mySide && isPieceGuardEnabled(p, guardConfig)) {
        hasAnyGuardedPiece = true;
        break;
      }
    }
  }
  if (!hasAnyGuardedPiece) return null;

  const oppMoves1 = getAllLegalMoves(boardAfterMyMove, enemySide);
  // If candidateMove immediately checkmates or stalemates opponent, never warn!
  if (oppMoves1.length === 0) return null;

  // Deep checkmate / sacrifice check: if candidateMove forces mate within 3 plies, never warn! (A04)
  if (candidateMove.gaveCheck) {
    const deepAfterMove = alphaBeta(boardAfterMyMove, 2, -Infinity, Infinity, enemySide, mySide);
    if (deepAfterMove >= 9000 || deepAfterMove >= baselineCp - 80) {
      return null;
    }
  }

  // Lazy evaluation of depth-3 safe alternatives: ONLY run when Step 1 or Step 2 actually detects a guarded-piece crisis!
  let cachedAltContext: {
    bestAltEval: ReturnType<typeof analyzeTopMoves>[number] | undefined;
    bestAlternative: Move;
    bestAltFollowUp: Move | undefined;
    isAlreadyBestSafeMove: boolean;
    boardAfterAltMove: BoardGrid;
  } | null = null;

  const getAltContext = () => {
    if (cachedAltContext) return cachedAltContext;
    const topList = analyzeTopMoves(boardBefore, mySide, 3, 8);
    const topSafeList = topList.filter(e => !e.isSelfTrap);
    const bestAltEval = topSafeList[0] || topList[0];
    const bestAlternative = bestAltEval?.move || candidateMove;
    const bestAltFollowUp = bestAltEval?.bestFollowUpMove;
    const isAlreadyBestSafeMove = Boolean(
      bestAltEval &&
        !bestAltEval.isSelfTrap &&
        bestAlternative.from.row === candidateMove.from.row &&
        bestAlternative.from.col === candidateMove.from.col &&
        bestAlternative.to.row === candidateMove.to.row &&
        bestAlternative.to.col === candidateMove.to.col
    );
    const { nextBoard: boardAfterAltMove } = applyMoveOnClone(
      boardBefore,
      bestAlternative.from,
      bestAlternative.to
    );
    cachedAltContext = {
      bestAltEval,
      bestAlternative,
      bestAltFollowUp,
      isAlreadyBestSafeMove,
      boardAfterAltMove
    };
    return cachedAltContext;
  };

  // ---------------------------------------------------------------------------
  // [단계 1] 바로 다음 수(상대 응수 1턴)에 내 보호 지정 기물(차/포/사/마/상)이 아무 이득 없이 죽는지 검사
  // - A04: 같은 칸 되잡기뿐 아니라 다른 칸 반격(포 미끼 후 상대 대차 포획, 차 희생 외통 등)까지 검증!
  // ---------------------------------------------------------------------------
  for (const oppM1 of oppMoves1) {
    if (!oppM1.captured) continue;
    const victim = oppM1.captured;
    if (victim.side !== mySide || !isPieceGuardEnabled(victim, guardConfig)) continue;

    const victimVal = CP_VALUES[victim.type];
    if (victimVal - myInitialGainCp < 280) continue;

    const { nextBoard: boardAfterOppCap } = applyMoveOnClone(boardAfterMyMove, oppM1.from, oppM1.to);

    const sameSquareRecapCp = computeRecaptureGainCp(
      boardAfterOppCap,
      oppM1.to,
      oppM1.piece,
      mySide
    );
    // If same-square recapture already prevents a >= 280cp net loss, skip expensive full-board counter-strike search
    if (victimVal - myInitialGainCp - sameSquareRecapCp < 280) continue;

    const counterStrikeGainCp = computeBestCounterStrikeGainCp(
      boardBefore,
      boardAfterOppCap,
      mySide,
      victimVal,
      myInitialGainCp
    );
    const bestRecoveryCp = Math.max(sameSquareRecapCp, counterStrikeGainCp);
    const netLossCp = victimVal - myInitialGainCp - bestRecoveryCp;

    if (netLossCp >= 280) {
      const { bestAlternative, bestAltFollowUp, isAlreadyBestSafeMove, boardAfterAltMove } = getAltContext();
      if (isAlreadyBestSafeMove) return null;

      // Verify whether `bestAlternative` actually saves the guarded piece or avoids >=280cp loss (A06)
      const isDistinctAlt =
        bestAlternative.from.row !== candidateMove.from.row ||
        bestAlternative.from.col !== candidateMove.from.col ||
        bestAlternative.to.row !== candidateMove.to.row ||
        bestAlternative.to.col !== candidateMove.to.col;

      const altOppMoves = getAllLegalMoves(boardAfterAltMove, enemySide);
      const stillLosesInAlt = altOppMoves.some(am => {
        if (!am.captured || !isPieceGuardEnabled(am.captured, guardConfig)) return false;
        const altGain = bestAlternative.captured ? CP_VALUES[bestAlternative.captured.type] : 0;
        if (CP_VALUES[am.captured.type] - altGain < 280) return false;
        const { nextBoard: bAltCap } = applyMoveOnClone(boardAfterAltMove, am.from, am.to);
        const altRecap = computeRecaptureGainCp(bAltCap, am.to, am.piece, mySide);
        if (CP_VALUES[am.captured.type] - altGain - altRecap < 280) return false;
        const altCounter = computeBestCounterStrikeGainCp(
          boardBefore,
          bAltCap,
          mySide,
          CP_VALUES[am.captured.type],
          altGain
        );
        return CP_VALUES[am.captured.type] - altGain - Math.max(altRecap, altCounter) >= 280;
      });

      const hasVerifiedSafeAlternative = isDistinctAlt && !stillLosesInAlt;

      const pieceNameKo = `${PIECE_NAMES_KO[victim.type][mySide]}(${PIECE_SCORES[victim.type]}점)`;
      const safeNotation = formatKoreanNotation(
        bestAlternative.piece,
        bestAlternative.from,
        bestAlternative.to,
        bestAlternative.captured
      );
      const enemyNotation = formatKoreanNotation(oppM1.piece, oppM1.from, oppM1.to, oppM1.captured);

      return {
        triggered: true,
        crisisType: 'immediate_1turn_loss',
        guardedPiece: victim,
        guardedPiecePos: oppM1.to,
        guardedPieceNameKo: pieceNameKo,
        netLossCp,
        enemyStrikeMove1: oppM1,
        recommendedSafeMove: bestAlternative,
        recommendedFollowUp: bestAltFollowUp,
        warningIsFree: true,
        hasVerifiedSafeAlternative,
        canDeductForSafeReveal: hasVerifiedSafeAlternative,
        moveKey,
        headline: `🛡️ [위기방지] 이대로 두면 내 '${pieceNameKo}'이(가) 위험해요!`,
        explanation: hasVerifiedSafeAlternative
          ? `상대가 [${enemyNotation}] 수로 내 '${pieceNameKo}'을(를) 잡으면 큰 손해입니다. 안전한 대안수 [${safeNotation}]을(를) 확인해 보세요!`
          : `상대가 [${enemyNotation}] 수로 내 '${pieceNameKo}'을(를) 위협하고 있습니다. (현재 완벽한 안전 회피수가 없어 방지권은 차감되지 않습니다.)`
      };
    }
  }

  // ---------------------------------------------------------------------------
  // [단계 2] 2수 앞 강제 양걸이 함정 탐지
  // ---------------------------------------------------------------------------
  const forcingOppMoves = oppMoves1
    .filter(m => m.gaveCheck || (m.captured && CP_VALUES[m.captured.type] >= 300))
    .slice(0, 8);

  for (const oppM1 of forcingOppMoves) {
    const oppGain1 = oppM1.captured ? CP_VALUES[oppM1.captured.type] : 0;
    const { nextBoard: boardAfterOpp1 } = applyMoveOnClone(boardAfterMyMove, oppM1.from, oppM1.to);

    const myReplies = getAllLegalMoves(boardAfterOpp1, mySide);
    if (myReplies.length === 0) continue;

    // Order recaptures of oppM1.to & captures first so safe branches prove escape on iteration 0
    myReplies.sort((a, b) => {
      const aRecap = a.to.row === oppM1.to.row && a.to.col === oppM1.to.col ? 2000 : 0;
      const bRecap = b.to.row === oppM1.to.row && b.to.col === oppM1.to.col ? 2000 : 0;
      const aCap = a.captured ? CP_VALUES[a.captured.type] : 0;
      const bCap = b.captured ? CP_VALUES[b.captured.type] : 0;
      return bRecap + bCap - (aRecap + aCap);
    });

    let canEscapeWithoutGuardedLoss = false;
    let worstFatalCapture: Move | undefined;
    let worstVictim: Piece | undefined;
    let worstVictimPos: Pos | undefined;
    let worstNetLoss = 0;
    let sampleMyReply: Move = myReplies[0];

    for (const myRep of myReplies) {
      const myRepGain = myRep.captured ? CP_VALUES[myRep.captured.type] : 0;
      const { nextBoard: boardAfterMyRep } = applyMoveOnClone(boardAfterOpp1, myRep.from, myRep.to);
      const oppMoves2 = getAllLegalMoves(boardAfterMyRep, enemySide);

      let guardedLostInThisBranch = false;
      for (const oppM2 of oppMoves2) {
        if (!oppM2.captured) continue;
        const v2 = oppM2.captured;
        if (v2.side !== mySide || !isPieceGuardEnabled(v2, guardConfig)) continue;

        const maxPossibleLoss = CP_VALUES[v2.type] + oppGain1 - myInitialGainCp - myRepGain;
        if (maxPossibleLoss < 300) continue;

        const { nextBoard: boardAfterOpp2 } = applyMoveOnClone(boardAfterMyRep, oppM2.from, oppM2.to);
        const recaptureGain2 = computeRecaptureGainCp(boardAfterOpp2, oppM2.to, oppM2.piece, mySide);
        if (maxPossibleLoss - recaptureGain2 < 300) continue;

        const counterGain2 = computeBestCounterStrikeGainCp(
          boardBefore,
          boardAfterOpp2,
          mySide,
          CP_VALUES[v2.type],
          myInitialGainCp + myRepGain
        );
        const netLoss = maxPossibleLoss - Math.max(recaptureGain2, counterGain2);

        if (netLoss >= 300) {
          guardedLostInThisBranch = true;
          if (netLoss > worstNetLoss) {
            worstNetLoss = netLoss;
            worstFatalCapture = oppM2;
            worstVictim = v2;
            worstVictimPos = oppM2.to;
            sampleMyReply = myRep;
          }
          break;
        }
      }

      if (!guardedLostInThisBranch) {
        canEscapeWithoutGuardedLoss = true;
        break;
      }
    }

    if (!canEscapeWithoutGuardedLoss && worstVictim && worstFatalCapture && worstVictimPos) {
      const { bestAltEval, bestAlternative, bestAltFollowUp, isAlreadyBestSafeMove } = getAltContext();
      if (isAlreadyBestSafeMove) return null;

      const isDistinctAlt =
        bestAlternative.from.row !== candidateMove.from.row ||
        bestAlternative.from.col !== candidateMove.from.col ||
        bestAlternative.to.row !== candidateMove.to.row ||
        bestAlternative.to.col !== candidateMove.to.col;

      const hasVerifiedSafeAlternative = isDistinctAlt && !bestAltEval?.isSelfTrap;
      const pieceNameKo = `${PIECE_NAMES_KO[worstVictim.type][mySide]}(${PIECE_SCORES[worstVictim.type]}점)`;
      const opp1Notation = formatKoreanNotation(oppM1.piece, oppM1.from, oppM1.to, oppM1.captured);
      const opp2Notation = formatKoreanNotation(
        worstFatalCapture.piece,
        worstFatalCapture.from,
        worstFatalCapture.to,
        worstFatalCapture.captured
      );
      const safeNotation = formatKoreanNotation(
        bestAlternative.piece,
        bestAlternative.from,
        bestAlternative.to,
        bestAlternative.captured
      );

      return {
        triggered: true,
        crisisType: 'collapse_2turn_trap',
        guardedPiece: worstVictim,
        guardedPiecePos: worstVictimPos,
        guardedPieceNameKo: pieceNameKo,
        netLossCp: worstNetLoss,
        enemyStrikeMove1: oppM1,
        myForcedReply: sampleMyReply,
        enemyFatalCaptureMove2: worstFatalCapture,
        recommendedSafeMove: bestAlternative,
        recommendedFollowUp: bestAltFollowUp,
        warningIsFree: true,
        hasVerifiedSafeAlternative,
        canDeductForSafeReveal: hasVerifiedSafeAlternative,
        moveKey,
        headline: `⚡ [2수 앞 양걸이 위기!] 이 수를 두면 상대 연타에 '${pieceNameKo}'이(가) 잡힙니다!`,
        explanation: `상대가 ① [${opp1Notation}] 강제수로 압박한 뒤 ② [${opp2Notation}] 수로 내 '${pieceNameKo}'을(를) 빼앗는 양걸이 함정입니다! 추천수 [${safeNotation}] 수로 방어하세요!`
      };
    }
  }

  return null;
}

export function detectTurnStartCrisisForGuardedPieces(
  board: BoardGrid,
  mySide: Side,
  guardConfig: CrisisGuardConfig
): CrisisInterventionResult | null {
  const endangeredAll = detectEndangeredNonPawnPieces(board, mySide);
  const guardedAlerts = endangeredAll.filter(a => isPieceGuardEnabled(a.piece, guardConfig));
  if (guardedAlerts.length === 0) return null;

  const topAlert = guardedAlerts[0];
  const hunsu = computeHunsuCoaching(board, mySide).hunsu;
  const rescueMove = hunsu?.step1Move;
  if (!rescueMove) return null;

  const { nextBoard: afterRescue } = applyMoveOnClone(board, rescueMove.from, rescueMove.to);
  const stillEndangeredAfterRescue = detectEndangeredNonPawnPieces(afterRescue, mySide).some(
    a => a.piece.id === topAlert.piece.id
  );
  const hasVerifiedSafeAlternative = !stillEndangeredAfterRescue;

  const enemyStrike: Move = {
    from: topAlert.attackerPos,
    to: topAlert.pos,
    piece: topAlert.attackerPiece,
    captured: topAlert.piece,
    notation: formatKoreanNotation(topAlert.attackerPiece, topAlert.attackerPos, topAlert.pos, topAlert.piece)
  };
  const rescueNotation = formatKoreanNotation(
    rescueMove.piece,
    rescueMove.from,
    rescueMove.to,
    rescueMove.captured
  );

  return {
    triggered: true,
    crisisType: 'immediate_1turn_loss',
    guardedPiece: topAlert.piece,
    guardedPiecePos: topAlert.pos,
    guardedPieceNameKo: topAlert.pieceNameKo,
    netLossCp: CP_VALUES[topAlert.piece.type],
    enemyStrikeMove1: enemyStrike,
    recommendedSafeMove: rescueMove,
    recommendedFollowUp: hunsu?.step2FollowUpMove,
    warningIsFree: true,
    hasVerifiedSafeAlternative,
    canDeductForSafeReveal: hasVerifiedSafeAlternative,
    moveKey: formatMoveKey(rescueMove),
    headline: `🛡️ [호신강기 위기감지 자동 발동!] 수호 지정 기물 '${topAlert.pieceNameKo}'이(가) 적의 표적이 되었습니다!`,
    explanation: `${topAlert.dangerReason} 상대방은 이 경고가 뜬 사실을 전혀 모릅니다. 지금 즉시 [${rescueNotation}] 수로 응수하여 소중한 기물을 지켜내세요!`
  };
}
